import React, { RefObject, useCallback, useMemo, useRef } from "react";
import ReactResizeDetector from "react-resize-detector";
import { mapCurrentActionToCursor } from "../helpers";

import { Dispatch, State, ZoomLevel } from "../StateManager/state";
import { JobState } from "../types";
import { Canvas } from "./Canvas";
import { centerOnZoom } from "./centerOnZoom";

type Props = Readonly<{
  autoShrink: boolean;
  dispatch: Dispatch;
  jobState: JobState;
  onSave: () => void;
  pageImageURIs: Array<string>;
  readonly: boolean;
  state: State;
}>;
interface IResizeDetectorCallbackArgs {
  height: number;
  width: number;
}
export const Renderer = ({
  autoShrink,
  dispatch,
  jobState,
  onSave,
  pageImageURIs,
  readonly,
  state,
}: Props) => {
  const scrollRef: RefObject<HTMLDivElement> = useRef(null);
  // if the user changes the zoom, we recentre the canvas on the screen
  centerOnZoom(scrollRef, state.zoom, state.editTable);
  const disableCanvas = useMemo((): boolean => {
    if (readonly) {
      return true;
    }
    switch (jobState) {
      case JobState.Default:
        return false;
      case JobState.Saving:
      case JobState.Submitting:
        return true;
    }
  }, [jobState, readonly]);
  const { activePage, pages } = state;
  const { currentAction } = pages[activePage];
  const cursor = useMemo(() => {
    if (!disableCanvas) {
      return mapCurrentActionToCursor(currentAction);
    }
    return "default";
  }, [disableCanvas, currentAction]);
  const resetZoom = useCallback(() => {
    dispatch({
      type: "CHANGE ZOOM LEVEL",
      zoom: ZoomLevel["1x"],
    });
  }, [dispatch]);
  return (
    <div className="renderer" style={{ cursor }}>
      <ReactResizeDetector handleHeight handleWidth>
        {({ height, width }: IResizeDetectorCallbackArgs) => (
          <div
            style={{
              height: `${height}px`,
              width: `${width}px`,
            }}
          >
            <div
              ref={scrollRef}
              style={{
                height: "100%",
                overflow: "auto",
                position: "relative",
                width: "100%",
              }}
            >
              <Canvas
                autoShrink={autoShrink}
                disabled={disableCanvas}
                dispatch={dispatch}
                height={height}
                imageURI={pageImageURIs[state.activePage]}
                onSave={onSave}
                parentScrollRef={scrollRef.current}
                resetZoom={resetZoom}
                state={state}
                width={width}
                zoom={state.zoom}
              />
            </div>
          </div>
        )}
      </ReactResizeDetector>
    </div>
  );
};
